/**
 * Image Workflows React Query Hooks
 * Data fetching and mutations for workflow management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { imageWorkflowsApi } from '@/lib/api/imageWorkflows';
import type {
  WorkflowCreate,
  WorkflowUpdate,
  ExecuteWorkflowRequest,
  WorkflowExecution,
} from '@/types/imageWorkflow';

// ============================================================================
// Query Keys
// ============================================================================

export const workflowKeys = {
  all: ['image-workflows'] as const,
  lists: () => [...workflowKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...workflowKeys.lists(), filters] as const,
  details: () => [...workflowKeys.all, 'detail'] as const,
  detail: (id: string) => [...workflowKeys.details(), id] as const,
  history: (id: string) => [...workflowKeys.detail(id), 'history'] as const,
  executions: () => [...workflowKeys.all, 'executions'] as const,
  execution: (id: string) => [...workflowKeys.executions(), id] as const,
  userRun: (executionId: string, runId: string) =>
    [...workflowKeys.execution(executionId), 'runs', runId] as const,
};

// ============================================================================
// Workflow Definition Hooks
// ============================================================================

/**
 * Hook to fetch all workflows with optional filters
 */
export function useWorkflows(params?: {
  is_active?: boolean;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: workflowKeys.list(params ?? {}),
    queryFn: () => imageWorkflowsApi.list(params),
  });
}

/**
 * Hook to fetch a single workflow
 */
export function useWorkflow(workflowId: string | null) {
  return useQuery({
    queryKey: workflowKeys.detail(workflowId!),
    queryFn: () => imageWorkflowsApi.get(workflowId!),
    enabled: !!workflowId,
  });
}

/**
 * Hook to fetch workflow version history
 */
export function useWorkflowHistory(
  workflowId: string | null,
  params?: { limit?: number; offset?: number }
) {
  return useQuery({
    queryKey: workflowKeys.history(workflowId!),
    queryFn: () => imageWorkflowsApi.getHistory(workflowId!, params),
    enabled: !!workflowId,
  });
}

/**
 * Hook to create a new workflow
 */
export function useCreateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: WorkflowCreate) => imageWorkflowsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
  });
}

/**
 * Hook to update a workflow
 */
export function useUpdateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workflowId, data }: { workflowId: string; data: WorkflowUpdate }) =>
      imageWorkflowsApi.update(workflowId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(variables.workflowId) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.history(variables.workflowId) });
    },
  });
}

/**
 * Hook to delete a workflow
 */
export function useDeleteWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workflowId: string) => imageWorkflowsApi.delete(workflowId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
  });
}

// ============================================================================
// Workflow Execution Hooks
// ============================================================================

/**
 * Hook to execute a workflow
 */
export function useExecuteWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ExecuteWorkflowRequest) => imageWorkflowsApi.execute(data),
    onSuccess: (result) => {
      // Invalidate executions list
      queryClient.invalidateQueries({ queryKey: workflowKeys.executions() });
      // Also invalidate the workflow to update execution_count
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(result.execution_id) });
    },
  });
}

/**
 * Hook to fetch executions list
 */
export function useExecutions(
  params?: {
    workflow_id?: string;
    status?: string;
    limit?: number;
    offset?: number;
  },
  options?: {
    enabled?: boolean;
    autoRefreshOnRunning?: boolean;
  }
) {
  const { enabled = true, autoRefreshOnRunning = false } = options ?? {};

  return useQuery({
    queryKey: [...workflowKeys.executions(), params],
    queryFn: () => imageWorkflowsApi.listExecutions(params),
    enabled,
    staleTime: 0, // No cache to avoid stale error responses
    gcTime: 0, // Don't keep old data
    // Conditional refetch: check if data has running executions
    refetchInterval: autoRefreshOnRunning
      ? (query) => {
          const hasRunning = query.state.data?.executions?.some(
            (exec) => exec.status === 'running' || exec.status === 'pending'
          );
          return hasRunning ? 3000 : false;
        }
      : false,
  });
}

/**
 * Hook to fetch a single execution with optional user runs
 */
export function useExecution(
  executionId: string | null,
  options?: {
    includeRuns?: boolean;
    autoRefresh?: boolean;
  }
) {
  const { includeRuns = false, autoRefresh = false } = options ?? {};

  return useQuery({
    queryKey: workflowKeys.execution(executionId!),
    queryFn: () => imageWorkflowsApi.getExecution(executionId!, includeRuns),
    enabled: !!executionId,
    staleTime: 10000, // 10 seconds
    // Auto-refresh while running
    refetchInterval: autoRefresh
      ? (query) => {
          const status = query.state.data?.status;
          return status === 'running' || status === 'pending' ? 2000 : false;
        }
      : false,
  });
}

/**
 * Hook to fetch user run details
 */
export function useUserRun(
  executionId: string | null,
  userRunId: string | null,
  options?: {
    includeSteps?: boolean;
    autoRefresh?: boolean;
  }
) {
  const { includeSteps = false, autoRefresh = false } = options ?? {};

  return useQuery({
    queryKey: workflowKeys.userRun(executionId!, userRunId!),
    queryFn: () => imageWorkflowsApi.getUserRun(executionId!, userRunId!, includeSteps),
    enabled: !!executionId && !!userRunId,
    staleTime: 10000,
    refetchInterval: autoRefresh
      ? (query) => {
          const status = query.state.data?.status;
          return status === 'running' || status === 'pending' ? 2000 : false;
        }
      : false,
  });
}

/**
 * Hook to resume a failed user run from a specific node
 * Reuses outputs from previously completed nodes
 */
export function useResumeUserRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      executionId,
      userRunId,
      resumeFromNodeKey,
      sync,
    }: {
      executionId: string;
      userRunId: string;
      resumeFromNodeKey?: string;
      sync?: boolean;
    }) =>
      imageWorkflowsApi.resumeUserRun(executionId, userRunId, {
        resumeFromNodeKey,
        sync,
      }),
    onSuccess: (_, variables) => {
      // Invalidate execution and user run queries
      queryClient.invalidateQueries({ queryKey: workflowKeys.execution(variables.executionId) });
      queryClient.invalidateQueries({
        queryKey: workflowKeys.userRun(variables.executionId, variables.userRunId),
      });
      queryClient.invalidateQueries({ queryKey: workflowKeys.executions() });
    },
  });
}

/**
 * Hook to poll execution until complete
 * Returns the final execution state
 */
export function usePollExecution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      executionId,
      onUpdate,
    }: {
      executionId: string;
      onUpdate?: (execution: WorkflowExecution) => void;
    }) => {
      return imageWorkflowsApi.pollExecution(executionId, {
        interval: 2000,
        timeout: 300000,
        onUpdate: (execution) => {
          // Update cache on each poll
          queryClient.setQueryData(workflowKeys.execution(executionId), execution);
          onUpdate?.(execution);
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.executions() });
    },
  });
}

// ============================================================================
// Convenience Hooks
// ============================================================================

/**
 * Hook for workflow execution with automatic polling
 * Combines execute + poll into one operation
 */
export function useExecuteAndPoll() {
  const executeWorkflow = useExecuteWorkflow();
  const pollExecution = usePollExecution();

  const execute = async (
    request: ExecuteWorkflowRequest,
    options?: {
      onStatusUpdate?: (execution: WorkflowExecution) => void;
    }
  ) => {
    // Start execution
    const startResponse = await executeWorkflow.mutateAsync(request);

    // Poll until complete
    const finalExecution = await pollExecution.mutateAsync({
      executionId: startResponse.execution_id,
      onUpdate: options?.onStatusUpdate,
    });

    return finalExecution;
  };

  return {
    execute,
    isExecuting: executeWorkflow.isPending,
    isPolling: pollExecution.isPending,
    isLoading: executeWorkflow.isPending || pollExecution.isPending,
    error: executeWorkflow.error || pollExecution.error,
  };
}
